import React, { useEffect, useState } from "react";
import { Box, Typography, Divider, Button, Skeleton } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { Link } from "react-router-dom";
import useSimApi from "src/hooks/useSimApi";
import useAuth from "src/context/AuthContext/useAuth";

const AUTH_MODEL_LABELS: Record<string, string> = {
  OAUTH2_CLIENT_CREDENTIALS: "OAuth 2.0 — Client Credentials",
  OAUTH2_AUTHORIZATION_CODE: "OAuth 2.0 — Authorization Code",
  BEARER_TOKEN: "Bearer Token (static)",
  BASIC: "Basic Auth",
  BASIC_WITH_SESSION_TOKEN: "Basic Auth with Session Token",
  QUERY_STRING: "Query String (API key)",
};

const CopyBtn: React.FC<{ text: string }> = ({ text }) => {
  const [done, setDone] = useState(false);
  return (
    <Box
      component="span"
      onClick={() => { navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); }}
      sx={{ cursor: "pointer", ml: 1, color: done ? "#22c55e" : "#9ca3af", "&:hover": { color: "#6b7280" }, display: "inline-flex", alignItems: "center", verticalAlign: "middle" }}
    >
      {done ? <CheckIcon sx={{ fontSize: 13 }} /> : <ContentCopyIcon sx={{ fontSize: 13 }} />}
    </Box>
  );
};

const Code: React.FC<{ children: React.ReactNode; block?: boolean; copy?: string }> = ({ children, block, copy }) =>
  block ? (
    <Box component="pre" sx={{ bgcolor: "#1e1b4b", color: "#e0e7ff", fontFamily: "monospace", fontSize: 13, p: 2, borderRadius: 1, overflowX: "auto", mt: 1, mb: 2, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
      {children}
    </Box>
  ) : (
    <Box component="span" sx={{ bgcolor: "#f3f4f6", color: "#4b5563", fontFamily: "monospace", fontSize: 13, px: 0.7, py: 0.15, borderRadius: 0.5 }}>
      {children}{copy && <CopyBtn text={copy} />}
    </Box>
  );

const P: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2" sx={{ color: "#374151", lineHeight: 1.8, mb: 1.5 }}>{children}</Typography>
);

const H2: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="h6" fontWeight={700} mb={1.5} sx={{ color: "#111827" }}>{children}</Typography>
);

const Val: React.FC<{ v: string | undefined; loading: boolean }> = ({ v, loading }) =>
  loading
    ? <Skeleton variant="text" width={180} sx={{ display: "inline-block" }} />
    : <Code copy={v}>{v || "—"}</Code>;

// ─── Auth-model specific sections ────────────────────────────────────────────

const OAuthClientCredentials: React.FC<{ base: string; clientId?: string; clientSecret?: string; scopes: string; loading: boolean }> = ({ base, clientId, clientSecret, scopes, loading }) => (
  <>
    <H2>How to get an access token</H2>
    <P>Post your credentials to the token endpoint. The simulator returns a Bearer token you include in all subsequent API calls.</P>
    <Code block>{`POST ${base}/oauth/token
Content-Type: application/json

{
  "grant_type": "client_credentials",
  "client_id": "${loading ? "<client_id>" : clientId}",
  "client_secret": "${loading ? "<client_secret>" : clientSecret}",
  "scope": "${scopes || "read"}"
}`}</Code>
    <P>Use the returned <Code>access_token</Code> as a Bearer token in all API requests:</P>
    <Code block>{`GET ${base}/your-endpoint
Authorization: Bearer <access_token>`}</Code>
    <H2>Strawbay console configuration</H2>
    <P>In the console application settings, set <Code>authorization_type</Code> to <Code copy="BEARER_TOKEN">BEARER_TOKEN</Code> and configure the token URL to <Code copy={`${base}/oauth/token`}>{`${base}/oauth/token`}</Code> with grant type <Code>client_credentials</Code>.</P>
  </>
);

const OAuthAuthCode: React.FC<{ base: string; clientId?: string; scopes: string; loading: boolean }> = ({ base, clientId, scopes, loading }) => (
  <>
    <H2>Step 1 — Redirect user to authorize</H2>
    <P>Direct the user's browser to the authorization endpoint. The simulator auto-approves and immediately redirects back with a code.</P>
    <Code block>{`GET ${base}/oauth/authorize
  ?response_type=code
  &client_id=${loading ? "<client_id>" : clientId}
  &redirect_uri=<your_redirect_uri>
  &scope=${scopes || "read"}
  &state=<optional_state>`}</Code>
    <H2>Step 2 — Exchange code for token</H2>
    <Code block>{`POST ${base}/oauth/token
Content-Type: application/json

{
  "grant_type": "authorization_code",
  "code": "<code_from_redirect>",
  "client_id": "${loading ? "<client_id>" : clientId}",
  "redirect_uri": "<your_redirect_uri>"
}`}</Code>
    <H2>Strawbay console configuration</H2>
    <P>Set <Code>authorization_type</Code> to <Code copy="BEARER_TOKEN">BEARER_TOKEN</Code>, auth URL to <Code copy={`${base}/oauth/authorize`}>{`${base}/oauth/authorize`}</Code>, and token URL to <Code copy={`${base}/oauth/token`}>{`${base}/oauth/token`}</Code>.</P>
  </>
);

const BearerToken: React.FC<{ base: string; clientId?: string; clientSecret?: string; scopes: string; loading: boolean }> = ({ base, clientId, clientSecret, scopes, loading }) => (
  <>
    <H2>Get a static token</H2>
    <P>Use the client credentials flow to obtain a long-lived token, then use it as a static Bearer token in your application configuration.</P>
    <Code block>{`POST ${base}/oauth/token
Content-Type: application/json

{
  "grant_type": "client_credentials",
  "client_id": "${loading ? "<client_id>" : clientId}",
  "client_secret": "${loading ? "<client_secret>" : clientSecret}",
  "scope": "${scopes || "read"}"
}`}</Code>
    <P>Copy the <Code>access_token</Code> from the response and set it directly in Strawbay console as a static Bearer token.</P>
    <H2>Strawbay console configuration</H2>
    <P>Set <Code>authorization_type</Code> to <Code copy="BEARER_TOKEN">BEARER_TOKEN</Code> and paste the token directly into the credentials field. Remember to refresh the token before it expires (TTL is configurable in <Link to="/settings" style={{ color: "#7166cf" }}>Settings</Link>).</P>
  </>
);

const BasicAuth: React.FC<{ base: string; clientId?: string; clientSecret?: string; loading: boolean }> = ({ base, clientId, clientSecret, loading }) => (
  <>
    <H2>Credentials to use</H2>
    <P>Configure your application with the simulator credentials as Basic Auth username and password.</P>
    <Box sx={{ mb: 2 }}>
      {[["Username", clientId], ["Password", clientSecret]].map(([label, val]) => (
        <Box key={label} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography sx={{ minWidth: 100, fontSize: 13, fontWeight: 600, color: "#6b7280" }}>{label}</Typography>
          <Val v={val} loading={loading} />
        </Box>
      ))}
    </Box>
    <P>The simulator will validate the Basic Auth header against these credentials on every request.</P>
    <Code block>{`GET ${base}/your-endpoint
Authorization: Basic ${loading ? "<base64(client_id:client_secret)>" : btoa(`${clientId}:${clientSecret}`)}`}</Code>
    <H2>Strawbay console configuration</H2>
    <P>Set <Code>authorization_type</Code> to <Code copy="BASIC">BASIC</Code>, username to your <Code copy={clientId}>{loading ? "<client_id>" : clientId}</Code> and password to your client secret.</P>
  </>
);

const BasicWithSession: React.FC<{ base: string; clientId?: string; clientSecret?: string; loading: boolean }> = ({ base, clientId, clientSecret, loading }) => (
  <>
    <H2>How it works</H2>
    <P>Authenticate first with Basic Auth to obtain a session token, then use that session token in subsequent requests.</P>
    <H2>Step 1 — Get session token</H2>
    <Code block>{`POST ${base}/oauth/token
Authorization: Basic ${loading ? "<base64(client_id:client_secret)>" : btoa(`${clientId}:${clientSecret}`)}
Content-Type: application/json

{ "grant_type": "client_credentials" }`}</Code>
    <H2>Step 2 — Use session token</H2>
    <Code block>{`GET ${base}/your-endpoint
Authorization: Bearer <session_token>`}</Code>
    <H2>Strawbay console configuration</H2>
    <P>Set <Code>authorization_type</Code> to <Code copy="BASIC_WITH_SESSION_TOKEN">BASIC_WITH_SESSION_TOKEN</Code> and configure both the Basic Auth credentials and the session token endpoint.</P>
  </>
);

const QueryString: React.FC<{ base: string; clientId?: string; clientSecret?: string; loading: boolean }> = ({ base, clientId, clientSecret, loading }) => (
  <>
    <H2>Get an API key</H2>
    <P>Use the client credentials flow to get a token, then pass it as a query parameter named <Code>api_key</Code> (or whatever key your application expects).</P>
    <Code block>{`POST ${base}/oauth/token
Content-Type: application/json

{
  "grant_type": "client_credentials",
  "client_id": "${loading ? "<client_id>" : clientId}",
  "client_secret": "${loading ? "<client_secret>" : clientSecret}"
}`}</Code>
    <P>Include the token as a query string parameter in all API calls:</P>
    <Code block>{`GET ${base}/your-endpoint?api_key=<access_token>`}</Code>
    <H2>Strawbay console configuration</H2>
    <P>Set <Code>authorization_type</Code> to <Code copy="QUERY_STRING">QUERY_STRING</Code> and configure the API key field with the token obtained above.</P>
  </>
);

// ─── Main component ───────────────────────────────────────────────────────────

const Docs: React.FC = () => {
  const { request } = useSimApi();
  const { connectorId, selectedConnectorId } = useAuth();
  const [config, setConfig] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    request("/api/v1/config")
      .then((r) => r.ok ? r.json() : null)
      .then(setConfig)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const activeConnectorId = selectedConnectorId ?? connectorId;
  const origin = window.location.origin;
  const base = activeConnectorId ? `${origin}/c/${activeConnectorId}` : origin;
  const swaggerUrl = `${base}/docs`;

  const authModel: string = config?.auth_model ?? "OAUTH2_CLIENT_CREDENTIALS";
  const clientId: string | undefined = config?.client_id;
  const clientSecret: string | undefined = config?.client_secret;
  const scopes: string = (config?.scopes ?? []).join(" ");

  const authContent = () => {
    switch (authModel) {
      case "OAUTH2_CLIENT_CREDENTIALS":
        return <OAuthClientCredentials base={base} clientId={clientId} clientSecret={clientSecret} scopes={scopes} loading={loading} />;
      case "OAUTH2_AUTHORIZATION_CODE":
        return <OAuthAuthCode base={base} clientId={clientId} scopes={scopes} loading={loading} />;
      case "BEARER_TOKEN":
        return <BearerToken base={base} clientId={clientId} clientSecret={clientSecret} scopes={scopes} loading={loading} />;
      case "BASIC":
        return <BasicAuth base={base} clientId={clientId} clientSecret={clientSecret} loading={loading} />;
      case "BASIC_WITH_SESSION_TOKEN":
        return <BasicWithSession base={base} clientId={clientId} clientSecret={clientSecret} loading={loading} />;
      case "QUERY_STRING":
        return <QueryString base={base} clientId={clientId} clientSecret={clientSecret} loading={loading} />;
      default:
        return <P>Unknown auth model.</P>;
    }
  };

  return (
    <Box sx={{ p: 4, maxWidth: 800 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 4 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} mb={0.5}>Documentation</Typography>
          <Typography variant="body2" color="text.secondary">
            How to configure and use the Strawbay Application Simulator
          </Typography>
        </Box>
        <Button variant="outlined" size="small" endIcon={<OpenInNewIcon fontSize="small" />} href={swaggerUrl} target="_blank" rel="noopener noreferrer"
          sx={{ textTransform: "none", borderColor: "#7166cf", color: "#7166cf", whiteSpace: "nowrap", ml: 2 }}>
          Open Swagger UI
        </Button>
      </Box>

      {/* What is the simulator */}
      <Box mb={4}>
        <H2>What is the Simulator?</H2>
        <P>The Strawbay Simulator is a configurable mock API server that lets you test integrations without connecting to a real third-party service. You define the endpoints you want to simulate, configure authentication, and point your application at the simulator instead of the real API.</P>
        <P>Each company has its own isolated simulator instance. Endpoints, credentials, and request logs are fully scoped to your account.</P>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* URLs */}
      <Box mb={4}>
        <H2>Simulator URLs</H2>
        {[
          ["API base URL", base],
          ["OAuth token endpoint", `${base}/oauth/token`],
          ["OAuth authorize endpoint", `${base}/oauth/authorize`],
        ].map(([label, url]) => (
          <Box key={label} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography sx={{ minWidth: 220, fontSize: 13, fontWeight: 600, color: "#6b7280" }}>{label}</Typography>
            <Code copy={url}>{url}</Code>
          </Box>
        ))}
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Credentials */}
      <Box mb={4}>
        <H2>Your credentials</H2>
        {[["client_id", clientId], ["client_secret", clientSecret]].map(([label, val]) => (
          <Box key={label} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography sx={{ minWidth: 220, fontSize: 13, fontWeight: 600, color: "#6b7280" }}>{label}</Typography>
            <Val v={val} loading={loading} />
          </Box>
        ))}
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Auth model — dynamic section */}
      <Box mb={4}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <H2>Authentication — {AUTH_MODEL_LABELS[authModel] ?? authModel}</H2>
          <Typography variant="caption" sx={{ color: "#9ca3af" }}>
            (change in <Link to="/settings" style={{ color: "#7166cf" }}>Settings</Link>)
          </Typography>
        </Box>
        {authContent()}
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Mock endpoints */}
      <Box mb={4}>
        <H2>Mock endpoints</H2>
        <P>Create mock endpoints on the <Link to="/endpoints" style={{ color: "#7166cf" }}>Endpoints</Link> page. Each endpoint has a method, path, HTTP status code, and a response body template.</P>
        <P>Path parameters use curly braces: <Code>{"/users/{id}/orders"}</Code></P>
        <Typography variant="subtitle2" fontWeight={700} mb={1} sx={{ color: "#374151" }}>Response templating</Typography>
        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: 13, mb: 2 }}>
          <Box component="thead">
            <Box component="tr" sx={{ bgcolor: "#f9fafb" }}>
              {["Variable", "Source", "Example"].map((h) => (
                <Box component="th" key={h} sx={{ p: "8px 12px", textAlign: "left", fontWeight: 600, borderBottom: "1px solid #e5e7eb", color: "#374151" }}>{h}</Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {[
              ["{{path.id}}", "Path parameter", "/users/42 → 42"],
              ["{{query.search}}", "Query string", "?search=foo → foo"],
              ["{{body.email}}", "JSON request body", '{"email":"x"} → x'],
              ["{{header.x-tenant}}", "Request header", "Header value"],
              ["{{random.uuid}}", "Random UUID", "a1b2c3d4-…"],
              ["{{random.int}}", "Random integer", "483920"],
            ].map(([v, d, e]) => (
              <Box component="tr" key={v as string} sx={{ "&:hover": { bgcolor: "#f9fafb" } }}>
                <Box component="td" sx={{ p: "8px 12px", borderBottom: "1px solid #f3f4f6", fontFamily: "monospace", color: "#4b5563" }}>{v}</Box>
                <Box component="td" sx={{ p: "8px 12px", borderBottom: "1px solid #f3f4f6" }}>{d}</Box>
                <Box component="td" sx={{ p: "8px 12px", borderBottom: "1px solid #f3f4f6", color: "#6b7280" }}>{e}</Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Divider sx={{ mb: 4 }} />

      <Box mb={4}>
        <H2>Request logs</H2>
        <P>All requests are logged on the <Link to="/logs" style={{ color: "#7166cf" }}>Request Logs</Link> page. Use <strong>Live stream</strong> to watch incoming requests in real time while debugging an integration.</P>
      </Box>
    </Box>
  );
};

export default Docs;
